import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Check, Zap, TrendingUp, Building2, ArrowLeft, Loader2, CheckCircle2,
  AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';
import { clearEntitlementsCache } from '../hooks/useEntitlements';
import { formatApiDetail, parseResponseJson } from '../lib/http';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '') ? process.env.REACT_APP_BACKEND_URL : '';
const PLAN_ORDER = ['basic', 'starter', 'growth'];
const FREE_PLANS = new Set(['basic']);

const PLAN_STYLES = {
  basic: { icon: Building2, gradient: 'from-emerald-500 to-teal-500', border: 'border-emerald-500/40', glow: 'shadow-emerald-500/20', check: 'text-emerald-400' },
  starter: { icon: Zap, gradient: 'from-blue-500 to-indigo-500', border: 'border-blue-500/40', glow: 'shadow-blue-500/20', check: 'text-blue-400' },
  growth: { icon: TrendingUp, gradient: 'from-violet-500 to-purple-600', border: 'border-violet-500/40', glow: 'shadow-violet-500/20', check: 'text-violet-400' },
};

const formatPrice = (planKey, region, pricing) => {
  if (FREE_PLANS.has(planKey)) return { label: 'Free', suffix: '' };
  const row = pricing?.[planKey];
  if (!row) return { label: '—', suffix: '' };
  if (region === 'in') return { label: `₹${Math.round(row.inr).toLocaleString('en-IN')}`, suffix: '/mo' };
  return { label: `$${row.usd}`, suffix: '/mo' };
};

const formatAddonPrice = (addon, region) => {
  if (region === 'in') return `₹${Math.round(addon.price_inr || 0).toLocaleString('en-IN')}/mo`;
  return `$${addon.price_usd || 0}/mo`;
};

export default function SubscriptionPlans({ setUser } = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [region, setRegion] = useState('us');
  const [provider, setProvider] = useState('stripe');
  const [mockSession, setMockSession] = useState(null);
  const [confirmingMock, setConfirmingMock] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);

  const plans = catalog?.plans || {};
  const pricing = catalog?.pricing || {};
  const billingMode = catalog?.billing_mode || 'mock';
  const addonModules = useMemo(() => {
    const raw = catalog?.addon_modules;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object') {
      return Object.entries(raw).map(([id, meta]) => ({ id, ...meta, kind: 'addon' }));
    }
    return [];
  }, [catalog]);

  const premiumAddons = useMemo(
    () => addonModules.filter((a) => (a.group || '').includes('Premium')),
    [addonModules],
  );
  const aiToolAddons = useMemo(
    () => addonModules.filter((a) => (a.group || '').includes('AI Tools')),
    [addonModules],
  );

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || typeof setUser !== 'function') return;
    try {
      clearEntitlementsCache();
      const meRes = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (meRes.ok) setUser(await meRes.json());
    } catch (_) { /* noop */ }
  }, [setUser]);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [catalogRes, currentRes, legacyRes] = await Promise.all([
        fetch(`${API_URL}/api/subscription/catalog`, { headers }),
        fetch(`${API_URL}/api/current-plan`, { headers }),
        fetch(`${API_URL}/api/plans`, { headers }),
      ]);
      let catalogData = catalogRes.ok ? await catalogRes.json() : null;
      if (!catalogData?.plans || !Object.keys(catalogData.plans).length) {
        const legacy = legacyRes.ok ? await legacyRes.json() : { plans: {} };
        catalogData = {
          billing_mode: 'live',
          plans: Object.fromEntries(
            Object.entries(legacy.plans || {}).map(([key, plan]) => [key, {
              key,
              name: plan.name,
              description: plan.description,
              highlights: plan.features || [],
              module_count: (plan.modules || []).length,
              modules_by_group: { Modules: (plan.modules || []).map((id) => ({ id, name: id })) },
            }]),
          ),
          pricing: { starter: { usd: 39, inr: 999 }, growth: { usd: 149, inr: 4999 } },
          addon_modules: [],
        };
      }
      const currentData = currentRes.ok ? await currentRes.json() : { active_plan: 'basic' };
      setCatalog(catalogData);
      setCurrentPlan(currentData.active_plan || null);
    } catch (e) {
      console.error(e);
      setError('Could not load plans. Restart the backend and refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);
  // PhonePe temporarily hidden — always Stripe (including India INR pricing).
  useEffect(() => { if (provider !== 'stripe') setProvider('stripe'); }, [provider]);

  const runCheckout = async (payload, successPath = '/subscription-plans') => {
    const token = localStorage.getItem('token');
    const origin = window.location.origin;
    const checkoutProvider = 'stripe';
    const res = await fetch(`${API_URL}/api/subscription/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...payload,
        region,
        provider: checkoutProvider,
        success_url: `${origin}${successPath}?payment=${checkoutProvider}&session_id={SESSION_ID}`,
        cancel_url: `${origin}${successPath}?payment=cancelled`,
      }),
    });
    const { data } = await parseResponseJson(res);
    if (!res.ok) throw new Error(formatApiDetail(data, 'Checkout failed'));
    if (data.success) return data;
    if (data.checkout_url) { window.location.href = data.checkout_url; return null; }
    if (data.redirect_url && data.mode === 'live') { window.location.href = data.redirect_url; return null; }
    if (data.mode === 'mock') { setMockSession(data); return data; }
    throw new Error(formatApiDetail(data, 'No checkout URL returned'));
  };

  const completeCheckoutReturn = useCallback(async (sessionId) => {
    const token = localStorage.getItem('token');
    if (!token || !sessionId) return;
    setBusyPlan('return');
    try {
      const statusRes = await fetch(`${API_URL}/api/subscription/checkout/${encodeURIComponent(sessionId)}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await parseResponseJson(statusRes);
      if (!statusRes.ok) throw new Error(formatApiDetail(data, 'Could not verify payment.'));
      if (data.success || data.payment_status === 'paid') {
        setResult(data);
        if (data.plan) setCurrentPlan(data.plan);
        await refreshUser();
        setSearchParams({});
      } else if (billingMode === 'mock') {
        setMockSession({ session_id: sessionId, provider: searchParams.get('payment') || 'stripe' });
      } else {
        setError('Payment pending — refresh shortly.');
      }
    } catch (e) {
      setError(e.message || 'Could not verify payment.');
    } finally {
      setBusyPlan('');
    }
  }, [billingMode, refreshUser, searchParams, setSearchParams]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment && payment !== 'cancelled') completeCheckoutReturn(sessionId);
    if (payment === 'cancelled') { setError('Payment cancelled.'); setSearchParams({}); }
  }, [searchParams, completeCheckoutReturn, setSearchParams]);

  const selectPlan = (planKey) => {
    setError(null);
    if (FREE_PLANS.has(planKey)) {
      activateFree(planKey);
      return;
    }
    const plan = plans[planKey];
    const price = formatPrice(planKey, region, pricing);
    setPaymentTarget({
      type: 'plan',
      planKey,
      title: `${plan?.name || planKey} Plan`,
      subtitle: plan?.selection_label || plan?.description || 'Monthly subscription — all dashboard modules included',
      amountLabel: `${price.label}${price.suffix ? '' : ''}`,
    });
  };

  const activateFree = async (planKey) => {
    setBusyPlan(planKey);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/activate-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: planKey }),
      });
      const { data } = await parseResponseJson(res);
      if (!res.ok) throw new Error(formatApiDetail(data, 'Activation failed'));
      setResult(data);
      setCurrentPlan(planKey);
      await refreshUser();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyPlan('');
    }
  };

  const confirmPayment = async () => {
    if (!paymentTarget) return;
    setBusyPlan(paymentTarget.planKey || paymentTarget.addonId);
    setPaymentTarget(null);
    try {
      const payload = paymentTarget.planKey
        ? { plan: paymentTarget.planKey }
        : { addon_id: paymentTarget.addonId };
      const data = await runCheckout(payload, paymentTarget.planKey ? '/subscription-plans' : '/snagcart/apps');
      if (data?.success) {
        setResult(data);
        if (data.plan) setCurrentPlan(data.plan);
        await refreshUser();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyPlan('');
    }
  };

  const confirmMockPayment = async () => {
    if (!mockSession?.session_id) return;
    setConfirmingMock(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/subscription/checkout/${encodeURIComponent(mockSession.session_id)}/confirm-mock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await parseResponseJson(res);
      if (!res.ok) throw new Error(formatApiDetail(data, 'Mock payment failed'));
      setResult(data);
      if (data.plan) setCurrentPlan(data.plan);
      setMockSession(null);
      await refreshUser();
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmingMock(false);
    }
  };

  const planCards = useMemo(() => PLAN_ORDER.filter((k) => plans[k]), [plans]);
  const layers = (catalog?.payment_model?.layers || []).filter((l) => l.id !== 'addons');

  const renderAddonCard = (addon) => (
    <div key={addon.id} className="rounded-xl border border-white/8 bg-black/25 p-4 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-white text-sm leading-snug">{addon.name}</p>
        <span className="shrink-0 text-xs font-semibold text-emerald-300">{formatAddonPrice(addon, region)}</span>
      </div>
      <p className="mt-1.5 text-xs text-zinc-500 leading-relaxed flex-1">{addon.desc}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="border-b border-white/10 bg-[#0d0d14] px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="h-4 w-px bg-white/10" />
        <span className="font-semibold text-sm">Snagcart Plans</span>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-3">Choose your plan</h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-sm leading-relaxed">
            {catalog?.payment_model?.headline
              || 'All three plans include every dashboard module. You select limits, credits, and support — not which modules you can open.'}
          </p>
          {currentPlan && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-sm text-emerald-300">
              <CheckCircle2 size={14} /> Active: <span className="capitalize font-semibold">{currentPlan}</span>
            </div>
          )}
          {layers.length > 0 && (
            <div className="mt-6 grid gap-3 text-left sm:grid-cols-3 max-w-3xl mx-auto">
              {layers.map((layer) => (
                <div key={layer.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <p className="text-xs font-semibold text-white">{layer.title}</p>
                  <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed">{layer.desc}</p>
                </div>
              ))}
            </div>
          )}
          {(catalog?.dashboard_modules || catalog?.payment_model?.dashboard_modules || []).length > 0 && (
            <div className="mt-6 max-w-3xl mx-auto rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left">
              <p className="text-xs font-semibold text-white mb-2">Included on every plan (left sidebar)</p>
              <div className="flex flex-wrap gap-1.5">
                {(catalog?.dashboard_modules || catalog?.payment_model?.dashboard_modules || []).map((name) => (
                  <span key={name} className="rounded-md border border-white/10 bg-black/30 px-2 py-0.5 text-[11px] text-zinc-400">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 flex gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300">
            <AlertCircle size={18} className="mt-0.5" /> {error}
          </div>
        )}
        {result && (
          <div className="mb-6 flex gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-200">
            <CheckCircle2 size={18} className="mt-0.5" />
            <div>
              <p className="font-medium">{result.message}</p>
              <button type="button" onClick={() => navigate('/snagcart/apps')} className="mt-2 text-sm underline">View Snagcart Modules</button>
            </div>
          </div>
        )}

        {mockSession && (
          <div className="mb-6 rounded-xl border border-sky-400/30 bg-sky-400/10 p-5">
            <p className="font-medium text-white">Mock payment — confirm to activate</p>
            <p className="mt-1 text-sm text-zinc-300">Simulates {mockSession.provider === 'phonepe' ? 'PhonePe' : 'Stripe card'} checkout.</p>
            <button type="button" onClick={confirmMockPayment} disabled={confirmingMock} className="mt-4 rounded-lg bg-sky-400 px-4 py-2 text-sm font-semibold text-black">
              {confirmingMock ? 'Processing…' : 'Confirm mock payment'}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-zinc-500" size={32} /></div>
        ) : planCards.length === 0 ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-8 text-center text-amber-100">
            No plans loaded. Ensure the backend is running on port 8002 and refresh.
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {planCards.map((planKey) => {
              const plan = plans[planKey];
              const style = PLAN_STYLES[planKey];
              const Icon = style.icon;
              const price = formatPrice(planKey, region, pricing);
              const isActive = currentPlan === planKey;
              const isBusy = busyPlan === planKey;
              const expanded = expandedPlan === planKey;
              const groups = plan.modules_by_group || {};

              return (
                <div key={planKey} className={`relative flex flex-col rounded-2xl border bg-[#0d0d14] p-6 ${style.border} ${style.glow}`}>
                  {planKey === 'starter' && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-500 px-3 py-0.5 text-xs font-bold">POPULAR</span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${style.gradient}`}>
                      <Icon size={18} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">{plan.name}</h2>
                      <p className="text-xs text-zinc-500">{plan.tagline || plan.description}</p>
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className={`text-3xl font-bold bg-gradient-to-r ${style.gradient} bg-clip-text text-transparent`}>{price.label}</span>
                    {price.suffix && <span className="text-zinc-500 text-sm ml-1">{price.suffix}</span>}
                  </div>
                  {plan.selection_label && (
                    <p className="mb-3 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-[11px] text-zinc-300 leading-snug">
                      {plan.selection_label}
                    </p>
                  )}
                  <p className="text-[11px] text-zinc-500 mb-4">
                    All dashboard modules
                    {(plan.included_credits?.lead_credits || plan.included_credits?.ai_studio_credits)
                      ? ` · ${plan.included_credits.lead_credits || 0} lead + ${plan.included_credits.ai_studio_credits || 0} AI credits`
                      : ''}
                  </p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">What you get with this selection</p>
                  <ul className="mb-4 space-y-2 flex-1">
                    {(plan.highlights || []).map((item) => (
                      <li key={item} className="flex gap-2 text-sm text-zinc-300">
                        <Check size={14} className={`mt-0.5 shrink-0 ${style.check}`} /> {item}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setExpandedPlan(expanded ? null : planKey)}
                    className="mb-4 flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
                  >
                    {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {expanded ? 'Hide' : 'Show'} module breakdown
                  </button>
                  {expanded && (
                    <div className="mb-4 max-h-48 overflow-y-auto rounded-lg border border-white/5 bg-black/30 p-3 text-xs space-y-3">
                      {Object.entries(groups).map(([group, mods]) => (
                        <div key={group}>
                          <p className="font-semibold text-zinc-400 mb-1">{group}</p>
                          <ul className="space-y-1 text-zinc-500">
                            {(mods || []).map((m) => <li key={m.id}>• {m.name}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={isActive || isBusy}
                    onClick={() => selectPlan(planKey)}
                    className={`w-full rounded-xl py-3 text-sm font-semibold ${isActive ? 'bg-white/10 text-zinc-500' : `bg-gradient-to-r ${style.gradient} text-white hover:opacity-90`}`}
                  >
                    {isBusy ? 'Processing…' : isActive ? 'Current plan' : FREE_PLANS.has(planKey) ? 'Activate free' : `Select ${plan.name}`}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {addonModules.length > 0 && (
          <div className="mt-16">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <h2 className="text-xl font-semibold">Optional extra AI tools</h2>
                <p className="mt-1 text-sm text-zinc-400 max-w-2xl">
                  Every left-sidebar module is already on Basic, Starter, and Growth. These are optional extra AI tools only — buy from Snagcart Modules if you need them.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/snagcart/apps')}
                className="text-sm text-violet-300 hover:text-violet-200 shrink-0"
              >
                Open Modules to purchase →
              </button>
            </div>

            {premiumAddons.length > 0 && (
              <div className="mb-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">Platform packs</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {premiumAddons.map(renderAddonCard)}
                </div>
              </div>
            )}

            {aiToolAddons.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">Extra AI tools</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {aiToolAddons.map(renderAddonCard)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <SubscriptionPaymentModal
        open={Boolean(paymentTarget)}
        onClose={() => setPaymentTarget(null)}
        target={paymentTarget}
        region={region}
        setRegion={setRegion}
        provider={provider}
        setProvider={setProvider}
        pricing={pricing}
        billingMode={billingMode}
        onConfirm={confirmPayment}
        busy={Boolean(busyPlan)}
      />
    </div>
  );
}
